import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { FabMenu } from '../../components/Overlay.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { MockBanner } from '../../components/DataState.tsx';
import { StatusPill, PriorityPill } from '../../components/Pill.tsx';
import {
  DEFAULT_SORT,
  NO_FILTER,
  filterNotes,
  isFiltered,
  scopeLabel,
  searchNotes,
  sortNotes,
} from '../../domain.ts';
import type { PrayerNote } from '../../domain.ts';
import { FilterBar } from './FilterBar.tsx';
import { FilterSheet } from './FilterSheet.tsx';
import { cardMeta } from './shared.ts';

/**
 * C1 — 메인. 전체 기도 노트.
 *
 * 탭에 들어오면 이 목록이 열립니다. 예전에는 ☰ 로 영역 서랍을 열고 세부항목을
 * 골라 들어가는 화면이 따로 있었지만 통째로 사라졌습니다 — 영역은 더 이상 노트를
 * 담는 서랍이 아니라 노트가 가진 항목 하나이고, 그래서 탐색이 아니라 조건으로
 * 다룹니다.
 *
 * 좁히는 길이 셋입니다.
 *
 *   검색   제목과 내용에서 찾습니다. 늘 나와 있습니다
 *   조건   영역 · 기도 상태 · 중요도 · 대표. 바깥 줄(C1-1 ~ C1-3)이 지름길입니다
 *   필터   오른쪽 위 시트(C1-4)가 정렬까지 한자리에서 만집니다
 *
 * **검색은 걸린 조건 안에서 찾습니다.** 검색한다고 조건이 풀리지 않으므로
 * 거르고 → 찾고 → 정렬하는 차례입니다. 머리글의 숫자와 켜진 버튼이 그대로
 * 남아, 지금 어느 범위를 뒤지는 중인지 보입니다.
 *
 * 셋 다 화면에서 합니다 — 서버는 내 노트를 최근 수정 순으로 줄 뿐입니다.
 * 조건을 켰다 껐다 할 때마다 서버를 다녀오면 펼침이 닫히는 사이 화면이 빕니다.
 */

interface Props {
  notes: PrayerNote[];
  onOpen: (id: string) => void;
  onNew: () => void;
}

export function NoteList({ notes, onOpen, onNew }: Props) {
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [filter, setFilter] = useState(NO_FILTER);
  const [query, setQuery] = useState('');
  const [sheet, setSheet] = useState(false);

  const scoped = filterNotes(notes, filter);
  const list = sortNotes(searchNotes(scoped, query), sort);
  const filtered = isFiltered(filter);

  return (
    <div className="screen">
      {/* ☰ 가 없습니다. 이 화면에서 나가는 문이 없어졌습니다 */}
      <NavBar title="Prayer Book" />

      <div className="screen__body">
        {/* 목업으로 돌고 있으면 목록보다 먼저 알립니다 */}
        <MockBanner />

        <SearchBox value={query} onChange={setQuery} placeholder="제목 · 내용 검색" />

        <div className="listhead__row">
          {/*
            숫자는 조건과 검색을 모두 거친 뒤의 것이고, 이름은 영역만 따라갑니다 —
            '기도중 기도 노트'처럼 적으면 읽기가 어색하고, 조건을 여럿 걸었을 때
            이름 하나로 다 담을 수도 없습니다 (C1-1-1).
          */}
          <p className="listhead__count">
            {scopeLabel(filter)} 기도 노트 <b>{list.length}</b>
          </p>
          {/* 제목과 같은 줄에 두어 줄 하나를 아꼈습니다 */}
          <button
            type="button"
            className={filtered ? 'sg sg--on filterbtn' : 'sg filterbtn'}
            aria-expanded={sheet}
            onClick={() => setSheet(true)}
          >
            <Icon name="filter" size={13} />
            필터
          </button>
        </div>

        <FilterBar sort={sort} onSort={setSort} filter={filter} onFilter={setFilter} />

        {list.length === 0 ? (
          <Empty filtered={filtered} scoped={scoped.length} />
        ) : (
          <div className="stack">
            {list.map((n) => (
              <button key={n.id} type="button" className="card" onClick={() => onOpen(n.id)}>
                {/*
                  상태를 첫 줄에 놓아 목록만 훑어도 진행 상황이 읽힙니다.
                  오른쪽 위는 중요도 전용이고 긴급 · 중요만 답니다 — 일반까지 달면
                  목록이 알약 밭이 되어 급한 것이 도리어 눈에 안 띕니다.
                */}
                <span className="card__pills card__pills--between">
                  <StatusPill status={n.status} />
                  {n.priority !== 'NORMAL' && <PriorityPill priority={n.priority} />}
                </span>
                <span className="card__title">{n.title}</span>
                {/* 영역 · 날짜 · D-day. Everyday 는 날짜 자리에 적습니다 */}
                <span className="card__meta">{cardMeta(n)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/*
        노트를 만드는 입구는 이것 하나뿐입니다. ＋ 는 바로 열지 않고 한 칸을
        둡니다 — 스친 손가락에 빈 노트가 생기지 않게 (C1-5).
      */}
      <FabMenu
        label="만들기"
        actions={[{ icon: 'doc', label: '새 기도 노트', onClick: onNew }]}
      />

      {sheet && (
        <FilterSheet
          sort={sort}
          filter={filter}
          onApply={(nextSortValue, nextFilter) => {
            setSort(nextSortValue);
            setFilter(nextFilter);
            setSheet(false);
          }}
          onClose={() => setSheet(false)}
        />
      )}
    </div>
  );
}

/**
 * 비어 있는 이유가 셋이라 안내도 셋입니다.
 *
 * "없습니다" 한 마디로 뭉뚱그리면, 조건을 걸어 둔 것을 잊은 사람은 노트가
 * 사라졌다고 읽습니다.
 *
 * `scoped` 는 **검색 전 · 조건 후**의 수라 무엇이 비운 것인지 여기서 갈립니다.
 * 0 이면 검색어와 무관하게 조건이 이미 다 걷어낸 것이고, 0 이 아닌데 목록이
 * 비었다면 남은 것은 검색어뿐입니다.
 */
function Empty({ filtered, scoped }: { filtered: boolean; scoped: number }) {
  if (scoped === 0) {
    return filtered ? (
      <div className="empty">
        <p className="empty__title">조건에 맞는 기도 노트가 없습니다</p>
        <p className="empty__hint">조건을 «전체»로 되돌리거나 필터에서 초기화해 보세요.</p>
      </div>
    ) : (
      <div className="empty">
        <p className="empty__title">아직 기도 노트가 없습니다</p>
        <p className="empty__hint">오른쪽 아래 ＋ 로 첫 기도 노트를 만들어 보세요.</p>
      </div>
    );
  }

  // 조건 안에는 노트가 있는데 목록이 비었습니다 — 걷어낸 것은 검색어입니다
  return (
    <div className="empty">
      <p className="empty__title">찾는 기도 노트가 없습니다</p>
      <p className="empty__hint">
        {filtered
          ? '걸린 조건 안에서만 찾습니다. 조건을 풀면 더 나올 수 있습니다.'
          : '제목과 내용에서 찾습니다. 다른 말로 찾아보세요.'}
      </p>
    </div>
  );
}
